import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { emailService } from '../../lib/emailService';

// Custom hooks
import { useTrackData } from '../../hooks/useTrackData';
import { useBookingValidation } from '../../hooks/useBookingValidation';
import { useBookingCalculation } from '../../hooks/useBookingCalculation';

// Components
import TrackTypeSelector from '../../components/booking/TrackTypeSelector';
import TrackInfoDisplay from '../../components/booking/TrackInfoDisplay';
import PricingOptions from '../../components/booking/PricingOptions';
import FloatingBookingModal from '../../components/booking/FloatingBookingModal';

export default function Services() {
  const { user, userProfile, isProfileComplete } = useAuth();
  const navigate = useNavigate();
  const [selectedTrackType, setSelectedTrackType] = useState<'drag' | 'drift'>('drag');
  const [selectedPricing, setSelectedPricing] = useState<any>(null);
  const [selectedDates, setSelectedDates] = useState<string[]>(['']);
  const [selectedStartTime, setSelectedStartTime] = useState<string>('');
  const [selectedEndTime, setSelectedEndTime] = useState<string>('');
  const [showAddOnsModal, setShowAddOnsModal] = useState(false);
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const [isMultiDateBooking, setIsMultiDateBooking] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Custom hooks
  const { 
    pricing, 
    trackInfo, 
    addOns, 
    loading, 
    lightingFee, 
    setLightingFee 
  } = useTrackData(selectedTrackType, userProfile);

  const {
    dateValidations,
    validatingDates,
    timeError,
    validateAllDates,
    validateTimeSelection,
    formatTime,
    hasInvalidDates,
    getInvalidDatesMessage,
    getDateValidation,
    setTimeError,
    formatDateForDatabase,
    formatDateForDisplay
  } = useBookingValidation(selectedTrackType, userProfile);

  const {
    paymentOption,
    setPaymentOption,
    calculateTotal,
    getDepositAmount,
    isBigEvent,
    getPaymentAmount,
    calculateLightingFee
  } = useBookingCalculation(
    selectedPricing, 
    selectedAddOns, 
    addOns, 
    lightingFee, 
    isMultiDateBooking, 
    selectedDates, 
    userProfile
  );

  useEffect(() => {
    // Check if promotor profile is complete - removed toast notification from here
    // to prevent duplicate messages
  }, [userProfile]);

  useEffect(() => {
    // Validate dates immediately when they change, regardless of time selection
    if (selectedDates.some(date => date.trim() !== '')) {
      validateAllDates(selectedDates);
    }
  }, [selectedDates, selectedTrackType]);

  useEffect(() => {
    if (selectedStartTime && selectedEndTime && selectedPricing) {
      validateTimeSelection(selectedStartTime, selectedEndTime, selectedPricing);
      updateLightingFee();
    }
  }, [selectedStartTime, selectedEndTime, selectedPricing]);

  // Helper function to format time to HH:MM:SS format for database
  const formatTimeForDatabase = (time: string): string => {
    if (!time) return '';
    // If time is already in HH:MM:SS format, return as is
    if (time.includes(':') && time.split(':').length === 3) {
      return time;
    }
    // If time is in HH:MM format, append :00
    if (time.includes(':') && time.split(':').length === 2) {
      return `${time}:00`;
    }
    return time;
  };

  const handleStartTimeChange = (time: string) => {
    setSelectedStartTime(time);
    
    // Auto-calculate end time based on package duration
    if (time && selectedPricing) {
      const [hours, minutes] = time.split(':').map(Number);
      const startDate = new Date();
      startDate.setHours(hours, minutes, 0, 0);
      
      const endDate = new Date(startDate.getTime() + (selectedPricing.duration_hours * 60 * 60 * 1000));
      
      // Format end time
      const endHours = endDate.getHours().toString().padStart(2, '0');
      const endMinutes = endDate.getMinutes().toString().padStart(2, '0');
      const endTimeString = `${endHours}:${endMinutes}`;
      
      setSelectedEndTime(endTimeString);
    }
  };

  const updateLightingFee = async () => {
    if (selectedEndTime && selectedPricing) {
      const fee = await calculateLightingFee(selectedEndTime, selectedPricing);
      setLightingFee(fee);
    }
  };

  const addDate = () => {
    setSelectedDates([...selectedDates, '']);
  };

  const removeDate = (index: number) => {
    if (selectedDates.length > 1) {
      setSelectedDates(selectedDates.filter((_, i) => i !== index));
    }
  };

  const updateDate = (index: number, value: string) => {
    const newDates = [...selectedDates];
    newDates[index] = value;
    setSelectedDates(newDates);
  };

  const toggleAddOn = (addOnId: string) => {
    setSelectedAddOns(prev => 
      prev.includes(addOnId)
        ? prev.filter(id => id !== addOnId)
        : [...prev, addOnId]
    );
  };

  const resetTimeSelections = () => {
    setSelectedStartTime('');
    setSelectedEndTime('');
    setSelectedDates(['']);
    setIsMultiDateBooking(false);
    setSelectedAddOns([]);
  };

  const openDateTimeModal = (pricing: any) => {
    if (userProfile?.role === 'promotor' && !isProfileComplete()) {
      // Only show the toast message here, not in the useEffect
      toast.error('Please complete your promotor profile before booking', {
        duration: 5000,
        action: {
          label: 'Complete Profile',
          onClick: () => navigate('/profile')
        }
      });
      return;
    }
    
    setSelectedPricing(pricing);
    setShowBookingModal(true);
  };

  const handleBooking = async () => {
    if (!user) {
      toast.error('Please login to book track time');
      navigate('/login', { state: { from: '/services' } });
      return;
    }

    // Check if promotor profile is complete
    if (userProfile?.role === 'promotor' && !isProfileComplete()) {
      toast.error('Please complete your promotor profile with CRIB and KVK numbers before booking');
      navigate('/profile');
      return;
    }

    if (!selectedPricing) {
      toast.error('Please select a pricing option');
      return;
    }

    const validDates = isMultiDateBooking 
      ? selectedDates.filter(date => date.trim() !== '')
      : [selectedDates[0]];

    if (validDates.length === 0) {
      toast.error('Please select at least one date');
      return;
    }

    if (!selectedStartTime || !selectedEndTime) {
      toast.error('Please select start and end times');
      return;
    }

    if (timeError) {
      toast.error('Please fix the time selection error before booking');
      return;
    }

    // Check for invalid dates
    if (hasInvalidDates()) {
      toast.error(getInvalidDatesMessage());
      return;
    }

    try {
      const baseAmount = selectedPricing.price + selectedAddOns.reduce((total, addOnId) => {
        const addOn = addOns.find(a => a.id === addOnId);
        return total + (addOn?.price || 0);
      }, 0) + lightingFee;

      const depositAmount = (isBigEvent() || paymentOption === 'deposit') ? selectedPricing.deposit_amount : 0;
      const totalAmount = baseAmount; // Base amount stays the same
      const remainingBalance = 0; // No remaining balance since deposit is additional

      // Create bookings for each selected date
      const bookingPromises = validDates.map(async (date) => {
        const formattedDate = formatDateForDatabase(date);
        const bookingData = {
          user_id: user.id,
          track_type: selectedTrackType,
          booking_type: 'individual' as const,
          time_slot: null,
          start_time: formatTimeForDatabase(selectedStartTime),
          end_time: formatTimeForDatabase(selectedEndTime),
          booking_date: formattedDate,
          duration_hours: selectedPricing.duration_hours,
          use_traction_compound: selectedPricing.use_traction_compound,
          total_amount: totalAmount,
          deposit_amount: depositAmount,
          deposit_paid: false,
          remaining_balance: remainingBalance,
          status: 'pending',
          payment_status: (isBigEvent() || paymentOption === 'deposit') ? 'deposit_pending' : 'pending'
        };

        const { data: booking, error: bookingError } = await supabase
          .from('track_bookings')
          .insert([bookingData])
          .select()
          .single();

        if (bookingError) throw bookingError;

        // Add selected add-ons for this booking
        if (selectedAddOns.length > 0) {
          const addOnData = selectedAddOns.map(addOnId => ({
            booking_id: booking.id,
            add_on_id: addOnId
          }));

          const { error: addOnsError } = await supabase
            .from('booking_add_ons')
            .insert(addOnData);

          if (addOnsError) throw addOnsError;
        }

        return booking;
      });

      const createdBookings = await Promise.all(bookingPromises);

      // Send email confirmation for the first booking (or all if needed)
      if (createdBookings.length > 0) {
        try {
          // Get user profile for email
          const { data: userProfile } = await supabase
            .from('users')
            .select('full_name, email, phone, company_name, crib_number, kvk_number')
            .eq('id', user.id)
            .single();

          if (userProfile) {
            const isPromotor = userProfile.company_name && userProfile.crib_number && userProfile.kvk_number;
            
            if (isPromotor) {
              // For promotors, send a different notification
              const promotorData = {
                customer_name: userProfile.full_name,
                company_name: userProfile.company_name || '',
                crib_number: userProfile.crib_number || '',
                kvk_number: userProfile.kvk_number || '',
                phone: userProfile.phone || '',
                email: userProfile.email || '',
                track_type: selectedTrackType === 'drag' ? 'Drag Strip' : 'Drift Track',
                booking_date: validDates.map(date => formatDateForDisplay(date)).join(', '),
                booking_time: `${formatTime(selectedStartTime)} - ${formatTime(selectedEndTime)}`,
                duration: selectedPricing.duration_hours,
                booking_type: isMultiDateBooking ? 'Multi-date Event' : 'Individual',
                traction_compound: selectedPricing.use_traction_compound,
                special_requests: '', // Add if you have special requests field
                total_amount: getPaymentAmount(),
                deposit_amount: depositAmount > 0 ? depositAmount : undefined
              };
              
              // Send notification to promotor
              await emailService.sendPromotorBookingNotification(
                user.id,
                userProfile.email,
                promotorData
              );
              
              // Send notification to admin
              await emailService.sendAdminPromotorBookingNotification(
                user.id,
                promotorData
              );
              
              toast.success(`Booking request submitted! Your booking will be reviewed by our team.`);
            } else {
              // For regular users, send the standard confirmation
              const emailData = {
                customer_name: userProfile.full_name,
                track_type: selectedTrackType === 'drag' ? 'Drag Strip' : 'Drift Track',
                booking_date: validDates.map(date => formatDateForDisplay(date)).join(', '),
                booking_time: `${formatTime(selectedStartTime)} - ${formatTime(selectedEndTime)}`,
                duration: selectedPricing.duration_hours,
                booking_type: isMultiDateBooking ? 'Multi-date Event' : 'Individual',
                traction_compound: selectedPricing.use_traction_compound,
                special_requests: '', // Add if you have special requests field
                total_amount: getPaymentAmount(),
                deposit_amount: depositAmount > 0 ? depositAmount : undefined
              };

              await emailService.sendTrackBookingConfirmation(
                user.id,
                userProfile.email,
                emailData
              );
              
              const dateText = validDates.length === 1 ? 'booking' : `${validDates.length} bookings`;
              toast.success(`${dateText} created successfully! Confirmation email sent.`);
            }
          }
        } catch (emailError) {
          console.error('Error sending confirmation email:', emailError);
          // Don't fail the booking if email fails
          toast.success('Booking created successfully! (Email delivery failed)', {
            description: 'Your booking is confirmed, but we could not send the confirmation email.',
            duration: 5000
          });
        }
      }

      setShowBookingModal(false);
      resetTimeSelections();
      navigate('/profile');
    } catch (error) {
      console.error('Error creating booking:', error);
      if (error instanceof Error) {
        // Check if it's an event buffer error
        if (error.message.includes('big event') && error.message.includes('buffer')) {
          toast.error(error.message);
        } else {
          toast.error(error.message);
        }
      } else {
        toast.error('Failed to create booking');
      }
    }
  };

  const isPromotor = userProfile?.role === 'promotor';
  const promotorProfileIncomplete = isPromotor && !isProfileComplete();

  // Filter pricing options for promotors - they can only see full day (8 hour) options
  const filteredPricing = isPromotor 
    ? pricing.filter(option => option.duration_hours === 8 || option.blocks_full_day)
    : pricing;

  return (
    <div className="min-h-screen bg-black py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">Track Booking</h1>
          <p className="text-xl text-gray-400">Choose your track type and booking duration</p>
          
          {promotorProfileIncomplete && (
            <div className="mt-4 p-4 bg-amber-900 border border-amber-700 rounded-lg max-w-2xl mx-auto">
              <p className="text-amber-200 text-sm">
                <strong>Attention Promotor:</strong> Please complete your profile with CRIB and KVK numbers before making a booking.
              </p>
              <button
                onClick={() => navigate('/profile')}
                className="mt-2 px-4 py-2 bg-amber-700 hover:bg-amber-600 text-white rounded-lg text-sm"
              >
                Complete Profile
              </button>
            </div>
          )}
        </div>

        {/* Track Type Selection */}
        <TrackTypeSelector 
          selectedTrackType={selectedTrackType}
          setSelectedTrackType={setSelectedTrackType}
          resetTimeSelections={resetTimeSelections}
        />

        {/* Track Description */}
        <TrackInfoDisplay trackInfo={trackInfo} />

        {/* Pricing Cards */}
        <PricingOptions 
          pricing={filteredPricing}
          selectedPricing={selectedPricing}
          setSelectedPricing={setSelectedPricing}
          loading={loading}
          openDateTimeModal={openDateTimeModal}
        />

        {/* Floating Booking Modal */}
        <FloatingBookingModal
          isOpen={showBookingModal}
          onClose={() => setShowBookingModal(false)}
          selectedPricing={selectedPricing}
          selectedDates={selectedDates}
          setSelectedDates={setSelectedDates}
          selectedStartTime={selectedStartTime}
          setSelectedStartTime={setSelectedStartTime}
          selectedEndTime={selectedEndTime}
          setSelectedEndTime={setSelectedEndTime}
          timeError={timeError}
          isMultiDateBooking={isMultiDateBooking}
          setIsMultiDateBooking={setIsMultiDateBooking}
          lightingFee={lightingFee}
          isPromotor={isPromotor}
          getDateValidation={getDateValidation}
          formatDateForDisplay={formatDateForDisplay}
          handleStartTimeChange={handleStartTimeChange}
          addDate={addDate}
          removeDate={removeDate}
          updateDate={updateDate}
          isBigEvent={isBigEvent}
          getDepositAmount={getDepositAmount}
          selectedAddOns={selectedAddOns}
          addOns={addOns}
          toggleAddOn={toggleAddOn}
          showAddOnsModal={showAddOnsModal}
          setShowAddOnsModal={setShowAddOnsModal}
          paymentOption={paymentOption}
          setPaymentOption={setPaymentOption}
          calculateTotal={calculateTotal}
          getPaymentAmount={getPaymentAmount}
          hasInvalidDates={hasInvalidDates}
          validatingDates={validatingDates}
          handleBooking={handleBooking}
        />
      </div>
    </div>
  );
}